import { useEffect } from 'react';

// Team member images
import chiZhangImg from '../assets/team/chi-zhang.png';
import hainingWangImg from '../assets/team/haining-wang.png';
import chenLiuImg from '../assets/team/chen-liu.png';
import mohamedChaabenImg from '../assets/team/mohamed-chaaben.png';

export function AboutPage() {
  useEffect(() => {
    document.title = 'À propos | GE-ROOM';

    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'GE-ROOM - Intelligence artificielle pour la conception spatiale. Découvrez notre mission et notre équipe.');
  }, []);

  const teamMembers = [
    {
      name: 'Chi ZHANG',
      role: 'Product manager',
      image: chiZhangImg,
      description: 'Product Manager with a successful track record in multiple software startups, proficient in full startup management and resource mobilization',
      highlights: ['software startups']
    },
    {
      name: 'Haining WANG',
      role: 'CEO',
      image: hainingWangImg,
      description: "With 10 years of experience, registration with the 'L'Ordre des Architectes,' and recognition in severals competitions, including the renovation of Singapore Airport Terminal 2, this expertise is applied to the design of furniture software.",
      highlights: ['design of furniture software']
    },
    {
      name: 'Chen LIU',
      role: 'Directeur Scientifique',
      image: chenLiuImg,
      description: 'PhD in Physics with 7 years of research and innovation experience in ENS Paris, Paris Saclay, Columbia University and in colaboration team of Giorgio Parisi (Nobel Prize 2022).',
      highlights: ['research and innovation']
    },
    {
      name: 'Mohamed CHAABEN',
      role: 'Directeur technique',
      image: mohamedChaabenImg,
      description: 'Over ten years of experience in IT consulting and software development. Skilled in team management and experienced in operating network architecture',
      highlights: ['IT consulting', 'software development']
    }
  ];

  const highlightText = (text: string, highlights: string[]) => {
    let result = text;
    highlights.forEach(highlight => {
      result = result.replace(highlight, `<span class="text-red-500">${highlight}</span>`);
    });
    return result;
  };

  return (
    <div className="pt-24 pb-16">
      {/* Hero Section */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl md:text-5xl font-bold text-black mb-12 tracking-tight">
          À propos de GE-ROOM
        </h1>

        <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
          <p>
            GE-ROOM est né d'un constat simple : nous vivons dans un monde en trois dimensions, mais nous continuons à le traduire avec des outils complexes, fragmentés et souvent inadaptés.
          </p>

          <p>
            Issu du monde de l'architecture et de la conception spatiale, le projet GE-ROOM s'est construit autour d'une même conviction : concevoir un espace ou concevoir un produit relève du même processus. On part du chaos, on observe les usages, et l'on cherche à transformer des systèmes complexes en quelque chose de lisible, fluide et évident.
          </p>

          <p>
            Depuis ses débuts, GE-ROOM explore comment l'intelligence artificielle peut devenir un véritable outil d'assistance à la création spatiale. À travers notre plateforme et nos expérimentations, notamment avec des projets comme Archi-Easy, nous cherchons à automatiser ce qui est répétitif, technique ou lourd, pour libérer du temps, de l'énergie et de l'attention au profit de la réflexion, du design et de la décision.
          </p>

          <p>
            Notre ambition à long terme est claire : supprimer les couches inutiles entre les idées et l'espace réel, et permettre à un plus grand nombre de professionnels — marques, architectes, designers, fabricants — de concevoir, visualiser et améliorer les espaces plus simplement, plus rapidement et plus intelligemment.
          </p>

          <p>
            GE-ROOM n'a pas vocation à remplacer l'expertise humaine, mais à l'amplifier. À rendre la lecture de l'espace plus accessible. Et à redonner à chacun la capacité de se projeter, comprendre et transformer son environnement.
          </p>
        </div>
      </section>

      {/* Team Section */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-black text-center mb-16 tracking-tight">
          TEAMS
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {teamMembers.map((member) => (
            <div key={member.name} className="text-center">
              {/* Avatar */}
              <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gray-100 overflow-hidden">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover"
                />
              </div>

              <h3 className="font-bold text-black text-lg">{member.name}</h3>
              <p className="text-gray-600 text-sm mb-4">{member.role}</p>

              <p
                className="text-gray-600 text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: highlightText(member.description, member.highlights) }}
              />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
